<script lang="ts">
  export let daysOnPlan: number = 0; // number_days_on_forecast_plan
  export let daysWithForecastedTraining: number = 0; // number of days with forecasted training (excluding rest days)
  export let missedDays: number = 0; // number_forecasted_training_missed_days
  export let performedDays: number | undefined = undefined; // optional override; else computed
  export let revealed: boolean = false;

  $: performed = performedDays ?? Math.max(0, daysWithForecastedTraining - missedDays);
  // Use scheduled training days for the adherence percentage guard
  $: pct = daysWithForecastedTraining > 0 ? Math.round((performed / daysWithForecastedTraining) * 100) : 0;
  $: clampedPct = Math.min(100, Math.max(0, pct));
</script>

<div class="relative w-[300px] h-[480px] rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_30px_rgba(251,86,7,0.12)]">
  <!-- Warm paper ticket background -->
  <div class="absolute inset-0 bg-[linear-gradient(160deg,#2a1f14_0%,#3a2a1d_45%,#2e2218_100%)]"></div>
  <div class="absolute inset-0 opacity-[0.08] bg-[radial-gradient(8px_8px_at_10%_20%,rgba(255,255,255,0.9)_0_1px,transparent_2px),radial-gradient(8px_8px_at_80%_70%,rgba(255,255,255,0.9)_0_1px,transparent_2px)]"></div>
  <!-- Perforated ticket look (context) -->
  <div class="absolute inset-x-4 top-8 h-[96px] rounded-xl bg-white/[0.04] border border-white/5 backdrop-blur-[2px] z-10">
    <div class="absolute inset-0 flex">
      <div class="flex-1 p-5 flex flex-col justify-center">
        <div class="text-[10px] leading-none uppercase tracking-[0.12em] opacity-70">Days on forecast plan</div>
        <div class="text-3xl font-semibold montserrat opacity-80">{daysOnPlan}</div>
      </div>
      <div class="w-[1px] my-4 bg-gradient-to-b from-white/10 via-transparent to-white/10 border-dashed border-l border-white/20" />
      <div class="w-28 p-5 flex flex-col justify-center items-end">
        <div class="text-[10px] leading-none uppercase tracking-[0.15em] opacity-70 text-right">Forecast training days</div>
        <div class="text-2xl font-semibold montserrat opacity-80">{daysWithForecastedTraining}</div>
      </div>
    </div>
    <!-- Punch holes along the separator -->
    <div class="absolute inset-y-4 left-1/2 -translate-x-1/2 flex flex-col justify-between items-center">
      {#each Array(6) as _, i}
        <div class="h-2 w-2 rounded-full bg-black/40 shadow-[inset_0_1px_2px_rgba(255,255,255,0.25)] ring-1 ring-white/10 hole" class:hole-pop={revealed} style={`--i:${i}`}></div>
      {/each}
    </div>
  </div>

  <!-- Emphasized stamp section for Training Performed / Missed (stacked stamps) -->
  <div class="absolute left-5 right-5 top-[165px] z-20 flex flex-col items-center gap-6">
    <div class="stamp stamp-performed stamp-long" class:stamp-in={revealed} style="--delay: 500ms">
      <div class="stamp-title">Training<br/>Performed</div>
      <div class="stamp-number">{performed}</div>
    </div>
    <div class="stamp stamp-missed stamp-short" class:stamp-in={revealed} style="--delay: 650ms">
      <div class="stamp-title">Training<br/>Missed</div>
      <div class="stamp-number small">{missedDays}</div>
    </div>
  </div>

  <!-- Tape progress strip at bottom -->
  <div class="absolute left-0 right-0 bottom-12 px-5 z-10">
    <div class="text-xs mb-2 opacity-80">Plan adherence</div>
    <div class="h-4 w-full rounded-full bg-white/10 relative overflow-hidden">
      <div class="absolute inset-0 bg-[linear-gradient(90deg,transparent_0_10px,rgba(255,255,255,0.08)_10px_12px)] bg-[length:16px_100%]"></div>
      <div class="h-full bg-[linear-gradient(90deg,#FB5607_0%,#FFD166_50%,#06D6A0_100%)] transition-[width] duration-700 ease-out"
           class:grow-in={revealed}
           style={`width:${clampedPct}%`}>
      </div>
    </div>
    <div class="mt-1 text-xs opacity-70">{pct}% of {daysWithForecastedTraining} scheduled training days</div>
  </div>

  <div class="absolute bottom-4 left-5 right-5 flex items-center justify-between text-[11px] opacity-75 z-10">
    <span class="uppercase tracking-widest">Ticket #TRAIN-HARD</span>
    <span class="uppercase tracking-widest">Admit One</span>
  </div>
</div>

<style>
  /* Emphasized ink stamp styling (rectangular white rubber stamp with impact) */
  .stamp { opacity: 0; position: relative; display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 12px; padding: 8px 12px; transform: rotate(-4deg); color: #fff;
    font-family: 'Impact', sans-serif;
    inset: -2px; border: 2px solid rgba(255,255,255,0.92); border-radius: 2px;
  }
  .stamp-long { margin-left: 24px; margin-right: auto; }
  .stamp-short { margin-left: auto; margin-right: 24px; }
  .stamp-title { font-size: 18px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; opacity: 0.98; text-shadow: 0 1px 0 rgba(0,0,0,0.35); }
  .stamp-number { color: #c4fdc4; font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 42px; line-height: 1; letter-spacing: 0.02em; text-shadow: 0 2px 0 rgba(0,0,0,0.42), 0 0 12px rgba(255,255,255,0.16); }
  .stamp-number.small { color: #fcb3b3; font-size: 30px; }
  /* All-white stamp: border, inner fill texture, and subtle scuff lines */
  .stamp::before { content: ""; position: absolute; inset: 2px; border: 1px solid rgba(255,255,255,0.92); border-radius: 1px; opacity: 0.9; box-shadow: 0 0 0 1px rgba(255,255,255,0.6) inset; }
  /* .stamp::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-image: url("/assets/images/stamp_overlay.jpeg");
    background-size: cover;
    mix-blend-mode: darken;
  } */
  /* .stamp::after { content: ""; position: absolute; inset: 0; border-radius: 6px; background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.22) 0 2px, transparent 3px 6px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.16) 0 2px, transparent 3px 8px);
    opacity: 0.28; mix-blend-mode: screen; } */
  /* Impact animation: drop, overshoot, press, settle; delayed by ~0.5s */
  .stamp-in { animation: stampdrop 720ms cubic-bezier(0.22, 0.95, 0.4, 1) var(--delay, 500ms) both; }
  .stamp-in::before { animation: ringflash 520ms ease-out var(--delay, 500ms) both; }
  .stamp-in::after { animation: scufffade 900ms ease-out var(--delay, 500ms) both; }
  @keyframes stampdrop {
    0% { transform: translateY(-24px) scale(0.92) rotate(-14deg); opacity: 0; filter: drop-shadow(0 0 0 rgba(0,0,0,0.0)); }
    45% { transform: translateY(0) scale(1.08) rotate(-6deg); opacity: 1; filter: drop-shadow(0 8px 18px rgba(0,0,0,0.52)); }
    70% { transform: translateY(0) scale(0.96, 0.98) rotate(-7deg); opacity: 0.9; }
    100% { transform: translateY(0) scale(1.0) rotate(-6deg); opacity: 0.9; }
  }
  @keyframes ringflash {
    0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.0) inset; }
    50% { box-shadow: 0 0 12px 2px rgba(255,255,255,0.55) inset; }
    100% { box-shadow: 0 0 0 1px rgba(255,255,255,0.65) inset; }
  }
  @keyframes scufffade {
    0% { opacity: 0; }
    40% { opacity: 0.4; }
    100% { opacity: 0.35; }
  }

  .hole { transform: scale(0.6); opacity: 0; }
  .hole-pop { animation: holepop 400ms ease-out calc(200ms + (var(--i) * 60ms)) both; }
  @keyframes holepop { to { transform: scale(1); opacity: 1; } }

  .grow-in { animation: widthgrow 900ms ease-out 300ms both; }
  @keyframes widthgrow { from { width: 0%; } }
</style>
